import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import ButtonPrimary from '../../../../components/button/ButtonPrimary';
import ButtonSecondary from '../../../../components/button/ButtonSecondary';
import Skeleton from '../../../../components/Skeleton';
import { getSuffererByID } from '../../../../controllers/SuffererController';
import { getPanicLogByID, updatePanicLog } from '../../../../controllers/PanicLogController';

const EditModal = ({ t }) => {
    const { id, user_id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true)
    const [name, setName] = useState('');
    const [gender, setGender] = useState('');
    const [email, setEmail] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [address, setAddress] = useState('');
    const [emergencyName, setEmergencyName] = useState('');
    const [emergencyRelation, setEmergencyRelation] = useState('');
    const [emergencyPhoneNumber, setEmergencyPhoneNumber] = useState('');
    const [status, setStatus] = useState('');
    const [createdAt, setCreatedAt] = useState('');
    const [updatedBy, setUpdatedBy] = useState('');
    const [updatedAt, setUpdatedAt] = useState('');
    
    useEffect(() => {
        const fetchRoles = async () => {
            try {
                const result = await getSuffererByID(user_id);
                setName(result.name);
                setGender(result.gender);
                setEmail(result.email);
                setPhoneNumber(result.phone_number);
                setAddress(result.address);
                setEmergencyName(result.emergency.name);
                setEmergencyRelation(result.emergency.relation);
                setEmergencyPhoneNumber(result.emergency.phone_number);
            } catch (error) {
                toast.error(error.message);
            };
        };
        
        fetchRoles();
    }, [user_id]);
    
    useEffect(() => {
        const fetchUser = async () => {
            try {
                const result = await getPanicLogByID(id);
                setStatus(result.status);
                setCreatedAt(result.created_at);
                setUpdatedBy(result.updated_by ?? '-');
                setUpdatedAt(result.updated_at);
            } catch (error) {
                toast.error(error.message);
            } finally {
                setLoading(false);
            };
        };
        
        fetchUser();
    }, [id]);
    
    const isDisabled = useMemo(() => {
        return !(status.trim());
    }, [status]);
    
    if (loading) {
        return (
            <Skeleton className='flex h-36 w-full rounded-lg' />
        ); 
    };
    
    const handleUpdate = async () => {
        try {
            await updatePanicLog(id, { status });
            navigate('/panic-log');
            toast.success('Panic log updated successfully');
        } catch (error) {
            toast.error(error.message);
        };
    };
    
    const handleBack = () => {
        navigate('/panic-log');
    };
    
    return (
        <div className='flex flex-col p-5 rounded-lg bg-white space-y-5'>
            {/* Sufferer Data */}
            <div className='space-y-5'>
                <span className='text-base md:text-lg font-medium'>{t('menu.sufferer')}:</span>
                <div className='flex flex-grow items-center'>
                    <span className='w-36 text-xs md:text-sm'>{t('common.name.title')}:</span>
                    <input
                        className={`
                            w-full px-3 py-2 rounded border border-platinum text-xs md:text-sm
                            hover:outline-none hover:border-grayX11
                            focus:outline-none focus:border-blackOlive
                        `}
                        type='text'
                        value={name}
                        disabled={true}
                    />
                </div>
                <div className='flex flex-grow items-center'>
                    <span className='w-36 text-xs md:text-sm'>{t('common.gender.title')}:</span>
                    <input
                        className={`
                            w-full px-3 py-2 rounded border border-platinum text-xs md:text-sm
                            hover:outline-none hover:border-grayX11
                            focus:outline-none focus:border-blackOlive
                        `}
                        type='text'
                        value={gender === 'Perempuan' ? t('common.gender.female') : t('common.gender.male')}
                        disabled={true}
                    />
                </div>
                <div className='flex flex-grow items-center'>
                    <span className='w-36 text-xs md:text-sm'>{t('common.email.title')}:</span>
                    <input
                        className={`
                            w-full px-3 py-2 rounded border border-platinum text-xs md:text-sm
                            hover:outline-none hover:border-grayX11
                            focus:outline-none focus:border-blackOlive
                        `}
                        type='text'
                        value={email}
                        disabled={true}
                    />
                </div>
                <div className='flex flex-grow items-center'>
                    <span className='w-36 text-xs md:text-sm'>{t('common.phoneNumber.title')}:</span>
                    <input
                        className={`
                            w-full px-3 py-2 rounded border border-platinum text-xs md:text-sm
                            hover:outline-none hover:border-grayX11
                            focus:outline-none focus:border-blackOlive
                        `}
                        type='text'
                        value={'+62' + phoneNumber}
                        disabled={true}
                    />
                </div>
                <div className='flex flex-grow items-center'>
                    <span className='w-36 text-xs md:text-sm'>{t('common.address.title')}:</span>
                    <textarea
                        className={`
                            w-full px-3 py-2 rounded border border-platinum text-xs md:text-sm
                            hover:outline-none hover:border-grayX11
                            focus:outline-none focus:border-blackOlive
                        `}
                        value={address}
                        disabled={true}
                    />
                </div>
            </div>
            
            {/* Emergency Contact */}
            <div className='space-y-5'>
                <span className='text-base md:text-lg font-medium'>{t('common.emergency.title')}:</span>
                <div className='flex flex-grow items-center'>
                    <span className='w-36 text-xs md:text-sm'>{t('common.name.title')}:</span>
                    <input
                        className={`
                            w-full px-3 py-2 rounded border border-platinum text-xs md:text-sm
                            hover:outline-none hover:border-grayX11
                            focus:outline-none focus:border-blackOlive
                        `}
                        type='text'
                        value={emergencyName}
                        disabled={true}
                    />
                </div>
                <div className='flex flex-grow items-center'>
                    <span className='w-36 text-xs md:text-sm'>{t('common.emergency.relation.title')}:</span>
                    <input
                        className={`
                            w-full px-3 py-2 rounded border border-platinum text-xs md:text-sm
                            hover:outline-none hover:border-grayX11
                            focus:outline-none focus:border-blackOlive
                        `}
                        type='text'
                        value={emergencyRelation}
                        disabled={true}
                    />
                </div>
                <div className='flex flex-grow items-center'>
                    <span className='w-36 text-xs md:text-sm'>{t('common.phoneNumber.title')}:</span>
                    <input
                        className={`
                            w-full px-3 py-2 rounded border border-platinum text-xs md:text-sm
                            hover:outline-none hover:border-grayX11
                            focus:outline-none focus:border-blackOlive
                        `}
                        type='text'
                        value={'+62' + emergencyPhoneNumber}
                        disabled={true}
                    />
                </div>
            </div>
            
            {/* Panic Log */}
            <div className='space-y-5'>
                <span className='text-base md:text-lg font-medium'>{t('panicLog.title')}:</span>
                <div className='flex flex-grow items-center'>
                    <span className='w-36 text-xs md:text-sm'>{t('common.status.title')}:</span>
                    <select
                        className={`
                            w-full px-3 py-2 rounded bg-white border border-platinum text-xs md:text-sm
                            hover:outline-none hover:border-grayX11
                            focus:outline-none focus:border-blackOlive
                        `}
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                    >
                        <option value='' disabled={status !== ''}>{t('common.status.placeholder')}</option>
                        <option value='Pending'>{t('common.status.pending')}</option>
                        <option value='Handling'>{t('common.status.handling')}</option>
                        <option value='Resolved'>{t('common.status.resolved')}</option>
                    </select>
                </div>
                <div className='flex flex-grow items-center'>
                    <span className='w-36 text-xs md:text-sm'>{t('common.created.title')}:</span>
                    <input
                        className={`
                            w-full px-3 py-2 rounded border border-platinum text-xs md:text-sm
                            hover:outline-none hover:border-grayX11
                            focus:outline-none focus:border-blackOlive
                        `}
                        type='text'
                        value={createdAt}
                        disabled={true}
                    />
                </div>
                <div className='flex flex-grow items-center'>
                    <span className='w-36 text-xs md:text-sm'>{t('common.updated.by')}:</span>
                    <input
                        className={`
                            w-full px-3 py-2 rounded border border-platinum text-xs md:text-sm
                            hover:outline-none hover:border-grayX11
                            focus:outline-none focus:border-blackOlive
                        `}
                        type='text'
                        value={updatedBy}
                        disabled={true}
                    />
                </div>
                <div className='flex flex-grow items-center'>
                    <span className='w-36 text-xs md:text-sm'>{t('common.updated.title')}:</span>
                    <input
                        className={`
                            w-full px-3 py-2 rounded border border-platinum text-xs md:text-sm
                            hover:outline-none hover:border-grayX11
                            focus:outline-none focus:border-blackOlive
                        `}
                        type='text'
                        value={updatedAt}
                        disabled={true}
                    />
                </div>
            </div>
            <div className='flex justify-end space-x-5'>
                <ButtonSecondary className='h-full w-24 md:w-32 md:text-sm' onClick={handleBack}>{t('common.action.back')}</ButtonSecondary>
                <ButtonPrimary className='h-full w-24 md:w-32 md:text-sm' isDisabled={isDisabled} onClick={handleUpdate}>{t('common.action.edit')}</ButtonPrimary>
            </div>
        </div>
    );
};

export default EditModal;
